import { cached, tracked } from '@glimmer/tracking';

import type { DSData } from 'webapp-types';
import {
	BlockVisualization,
	ChartLegendPosition,
	CompareFunction,
	CompareType,
	DataSelectType,
	OrderDirection,
	SeriesVisualization,
	ShownLabels,
} from 'webapp/enums/enums';
import { getAnomalyVariant } from 'webapp/utils/anomaly-detection';
import { equal, sortBy } from 'webapp/utils/common';
import {
	calculateAndFormatChange,
	calculateChange,
	getIndicatorClass,
	hashCode,
} from 'webapp/utils/number';

import type { AnyVisualization } from 'webapp/models-custom/block-setup';
import type DataSelect from 'webapp/models-custom/data-select';
import type DsDataOutputTopLevel from 'webapp/models-custom/ds-data/output/top';
import StatusObject from 'webapp/models-custom/status-object';
import VisualizationBase from 'webapp/models-custom/visualization/base';
import type { YAxisScaleManual, YAxisScaleNotOff, YAxisScaleOff } from 'webapp/models/block';
import DChartComparePoint from 'webapp/d-chart/point/compare-point';
import type { PointData } from 'webapp/d-chart/point/primary-point';
import DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';
import type { SeriesData } from 'webapp/d-chart/series/series';
import DChartSeries from 'webapp/d-chart/series/series';

interface NumberPointAndOutputAndChartOutputSet {
	numberPoint?: DChartPrimaryPoint;
	chartOutput?: DsDataOutputTopLevel;
	numberOutput?: DsDataOutputTopLevel;
	title?: string;
}

const RecordLimit = 15;

export default class VisualizationLineChart extends VisualizationBase {
	visualizationType = BlockVisualization.LINE_CHART;

	recordLimit = RecordLimit;

	showAnnotations = true;

	// ------- visualization specifics --------

	get isLegendOff(): boolean {
		return this.block.legendPosition === ChartLegendPosition.OFF;
	}

	get isLegendBottom(): boolean {
		return !this.isLegendOff;
	}

	get showTotalsOnStacked() {
		return this.block.showTotals && this.blockSetup.dataSelectsShown.some((ds) => ds.isStacked);
	}

	get dataSelectsOnSecondAxis(): number[] {
		return this.blockSetup.dataSelectsShown
			.filter((item) => item.yAxisIndex === 1)
			.map((item) => item.numericId) as number[];
	}

	set dataSelectsOnSecondAxis(dataSelectIds: number[]) {
		this.blockSetup.dataSelectsShown.forEach((ds) => {
			ds.yAxisIndex = dataSelectIds.includes(ds.numericId!) ? 1 : 0;
		});
	}

	get isYAxisFromZero(): boolean {
		return !!(this.block.yAxisScale1 as YAxisScaleOff | undefined)?.disabled;
	}

	get isYAxis2FromZero(): boolean {
		return !!(this.block.yAxisScale2 as YAxisScaleOff | undefined)?.disabled;
	}

	get isYAxisMinAuto(): boolean {
		const min = (this.block.yAxisScale1 as YAxisScaleNotOff | undefined)?.min;
		return typeof min !== 'number';
	}

	get isYAxisMaxAuto(): boolean {
		const max = (this.block.yAxisScale1 as YAxisScaleNotOff | undefined)?.max;
		return typeof max !== 'number';
	}

	get yAxisMin(): number | null {
		return this.isYAxisMinAuto ? null : (this.block.yAxisScale1 as YAxisScaleManual).min;
	}

	get yAxisMax(): number | null {
		return this.isYAxisMaxAuto ? null : (this.block.yAxisScale1 as YAxisScaleManual).max;
	}

	get isYAxisReversed(): boolean {
		return !!this.block.yAxisScale1?.reverse;
	}

	get isYAxis2MinAuto(): boolean {
		const min = (this.block.yAxisScale2 as YAxisScaleNotOff | undefined)?.min;
		return typeof min !== 'number';
	}

	get isYAxis2MaxAuto(): boolean {
		const max = (this.block.yAxisScale2 as YAxisScaleNotOff | undefined)?.max;
		return typeof max !== 'number';
	}

	get yAxis2Min(): number | null {
		return this.isYAxis2MinAuto ? null : (this.block.yAxisScale2 as YAxisScaleManual).min;
	}

	get yAxis2Max(): number | null {
		return this.isYAxis2MaxAuto ? null : (this.block.yAxisScale2 as YAxisScaleManual).max;
	}

	get isYAxis2Reversed(): boolean {
		return !!this.block.yAxisScale2?.reverse;
	}

	override getSortedSecondaryDataSelectsForDataSelect(
		primaryMainDataSelect: DataSelect,
	): DataSelect[] {
		const chartDataSelects = sortBy(
				this.getSecondarySeriesForChartDs(primaryMainDataSelect),
				'type',
			),
			numberDataSelect = this.getNumberForChartDs(primaryMainDataSelect);
		const dataSelects = [...chartDataSelects];
		if (numberDataSelect) {
			dataSelects.push(numberDataSelect);
		}
		return dataSelects;
	}

	override getDataSelectsByColorGroups() {
		// used for determining color validity
		// we can have same color on line and bar at the same time
		// so we divide by visualization
		const lineDataSelects: DataSelect[] = [],
			barDataSelects: DataSelect[] = [];
		this.blockSetup.dataSelects.forEach((ds) => {
			if (
				(this.visualizationType === BlockVisualization.BAR_CHART && !ds.visualization) ||
				ds.visualization === SeriesVisualization.BAR
			) {
				barDataSelects.push(ds);
			} else {
				lineDataSelects.push(ds);
			}
		});
		return [lineDataSelects, barDataSelects];
	}

	// ------- visualization - data --------

	@tracked numberPointAndChartOutputPairs: NumberPointAndOutputAndChartOutputSet[] = [];

	@tracked series: DChartSeries[] = [];

	@cached
	override get statusObjects(): StatusObject[] {
		const statusObjects: StatusObject[] = [];

		// all numbers and their charts are marked as number
		// everything else as series
		const dataSelects = this.blockSetup.dataSelects,
			dsForNumbers: DataSelect[] = [],
			dsForChart: DataSelect[] = [];

		dataSelects.forEach((ds) => {
			if (ds.limitations.isNumber) {
				dsForNumbers.push(ds);
			} else if (ds.isPrimary) {
				dsForChart.push(ds);
			} else {
				const mainDs = dataSelects.find((dd) => dd.id === String(ds.updateFrom));
				if (mainDs?.limitations.isNumber) {
					dsForNumbers.push(ds);
				} else {
					dsForChart.push(ds);
				}
			}
		});
		dsForNumbers.forEach((ds) => {
			const sos = ds.statusObjects;
			sos.forEach((so) => {
				so.dataLocation = 'number';
			});
			statusObjects.push(...sos);
		});
		dsForChart.forEach((ds) => {
			const sos = ds.statusObjects;
			sos.forEach((so) => {
				so.dataLocation = 'series';
			});
			statusObjects.push(...sos);
		});

		return StatusObject.eliminateDuplicates(statusObjects, true);
	}

	override updateDataCache() {
		this.numberPointAndChartOutputPairs = this.getNumberPointAndChartOutputPairs();
		this.numberPoints = this.prepareNumberPoints();
		this.series = this.getSeries();
		this.updateColorIndexes();
	}

	override clearDataCache() {
		this.numberPointAndChartOutputPairs = [];
		this.numberPoints = [];
		this.series = [];
	}

	updateColorIndexes() {
		// not using updateColorIndexesForItems bcos the logic here is more complex

		const colorIndexOrder = this.blockSetup.allAvailableColorsIndexes,
			usedColorIndexes: number[] = [];

		const numberPoints = this.numberPoints,
			series = this.series,
			mainSeriesCount = series.filter((s) => s.isMain).length;

		const doesSecondarySeriesHaveColor = (s: DChartSeries) => {
			return !s.isTheAggregatedTotal && (s.isForecast || s.isCompare || mainSeriesCount > 1);
		};

		const customColorClassMap = this.blockSetup.customColorClassMap;

		// 1. set user-defined colors first, and mark them as used
		//    numbers first
		numberPoints.forEach((p) => {
			const metricSelectionData = p.metricSelectionData;
			if (metricSelectionData) {
				const color = this.blockSetup.getColorForMetricSelectionData(metricSelectionData);
				if (typeof color === 'number') {
					p.colorIndexOrClass = color;
					usedColorIndexes.push(color);
				} else if (typeof color === 'string') {
					p.colorIndexOrClass = customColorClassMap[color] || '';
				}
			}
		});

		//    series after
		series.forEach((s) => {
			if (s.isMain || doesSecondarySeriesHaveColor(s)) {
				const metricSelectionData = s.metricSelectionData;
				if (metricSelectionData) {
					const color =
						this.blockSetup.getColorForMetricSelectionData(metricSelectionData);
					if (typeof color === 'number') {
						s.colorIndexOrClass = color;
						usedColorIndexes.push(color);
					} else if (typeof color === 'string') {
						s.colorIndexOrClass = customColorClassMap[color] || '';
					}
				}
			} else if (s.isForecastOptimistic || s.isForecastPessimistic) {
				s.colorIndexOrClass = `forecast-${s.type}`;
			} else if (s.isAnomaly) {
				// if lower and upper values have the same value, areasplinerange is not shown (stroke is hidden in this type)
				const areAllLowerAndUpperValueSame = s.points.every((p) => {
					const anomalyData = p.anomalyData;

					return (
						typeof anomalyData?.lowerValue === 'number' &&
						typeof anomalyData?.upperValue === 'number' &&
						anomalyData.lowerValue === anomalyData.upperValue
					);
				});

				s.colorIndexOrClass = `anomaly${areAllLowerAndUpperValueSame ? ' stroke' : ''}`;
			}
		});

		// 2. Group series with its number for use later, and get a count of total colors needed
		const numbersWithoutColor = numberPoints.filter((n) => n.hasNoColorIndexOrClass),
			seriesWithoutColor = series.filter((s) => s.hasNoColorIndexOrClass),
			mainSeriesWithoutNumber: DChartSeries[] = [],
			secondarySeriesWithoutPrimary: DChartSeries[] = [];

		// key: source of the color, value: array of the series that inherits it
		// so either number: primarySeries, or primarySeries: secondarySeries
		// series without colors are skipped
		const coloredPairs = new WeakMap<DChartSeries | DChartPrimaryPoint, DChartSeries[]>();

		const addToColoredPairs = (key: DChartSeries | DChartPrimaryPoint, value: DChartSeries) => {
			let series = coloredPairs.get(key);
			if (!series) {
				series = [];
			}
			series.push(value);
			coloredPairs.set(key, series);
		};

		const setColorsFromColoredPairs = (
			key: DChartSeries | DChartPrimaryPoint,
			colorIndex: number,
		) => {
			key.colorIndexOrClass = colorIndex;
			const mainSeriesForSameColor = coloredPairs.get(key);
			if (mainSeriesForSameColor) {
				mainSeriesForSameColor.forEach((mainSeries) => {
					setColorsFromColoredPairs(mainSeries, colorIndex);
				});
			}
		};

		seriesWithoutColor.forEach((s) => {
			if (s.isMain) {
				const sData = s.metricSelectionData;
				if (sData) {
					const matchingNumber = numberPoints.find((p) => {
						const pData = p.metricSelectionData;
						return (
							pData?.primaryDsId === sData.dsId &&
							equal(pData.dimensionValues, sData.dimensionValues, true) &&
							pData.unit === sData.unit
						);
					});
					if (matchingNumber) {
						addToColoredPairs(matchingNumber, s);
					} else {
						mainSeriesWithoutNumber.push(s);
					}
				} else {
					mainSeriesWithoutNumber.push(s);
				}
			} else if (doesSecondarySeriesHaveColor(s)) {
				if (s.primarySeries) {
					addToColoredPairs(s.primarySeries, s);
				} else {
					secondarySeriesWithoutPrimary.push(s);
				}
			}
		});

		const colorsNeeded =
			numbersWithoutColor.length +
			mainSeriesWithoutNumber.length +
			secondarySeriesWithoutPrimary.length;

		// 3. then, see which colors are left for free using, and get a dispersion
		const colorsLeft = Array.from(colorIndexOrder).filter((c) => !usedColorIndexes.includes(c)),
			dispersedColors = this.getDispersedColors(colorsLeft, colorsNeeded);

		// 3. assign the colors to the items in coloredPairs
		//    primary series from numbers, secondary series from primary
		//    we iterate over numbers first bcos they're sorted
		numbersWithoutColor.forEach((p, idx) => {
			const colorIndex = dispersedColors[idx]!;
			usedColorIndexes.push(colorIndex);
			setColorsFromColoredPairs(p, colorIndex);
		});

		const dispersedColorsLeft = dispersedColors.filter((c) => !usedColorIndexes.includes(c));

		// 4. assign the rest of the colors to the mainSeriesWithoutNumber / secondarySeriesWithoutPrimary
		[...mainSeriesWithoutNumber, ...secondarySeriesWithoutPrimary].forEach((s) => {
			const colorIndex = dispersedColorsLeft.shift();
			if (typeof colorIndex === 'number') {
				setColorsFromColoredPairs(s, colorIndex);
			}
		});
	}

	private getNumberPointAndChartOutputPairs(): NumberPointAndOutputAndChartOutputSet[] {
		const pairs = this.getNumberPointAndChartOutputPairsUnsorted();

		const sortField = this.block.sortField,
			sortOrder = this.block.sortOrder || OrderDirection.DESC;
		pairs.sort((a, b) => {
			return DChartPrimaryPoint.doPointCompare(
				a.numberPoint,
				b.numberPoint,
				sortField,
				sortOrder,
			);
		});

		return pairs;
	}

	private getNumberPointAndChartOutputPairsUnsorted(): NumberPointAndOutputAndChartOutputSet[] {
		const pairs: NumberPointAndOutputAndChartOutputSet[] = [];

		this.blockSetup.sortedDataSelectsShown.forEach((seriesDs) => {
			if (seriesDs.dataRoot) {
				const numberDs = this.getNumberForChartDs(seriesDs),
					numberOutputs = numberDs?.dataRoot?.outputs || [];

				let chartOutputs = Array.from(seriesDs.dataRoot.outputs);

				numberOutputs.forEach((numberOutput) => {
					const point = this.createDChartPointsFromOutput(numberOutput)[0];
					const pair: NumberPointAndOutputAndChartOutputSet = {
						numberOutput,
						numberPoint: point,
					};
					const chartOutputForThisNumber = chartOutputs.find((chartOutput) => {
						return (
							equal(
								chartOutput.dimensionValues,
								numberOutput.dimensionValues,
								true,
							) && chartOutput.unit === numberOutput.unit
						);
					});
					if (chartOutputForThisNumber) {
						pair.chartOutput = chartOutputForThisNumber;
						chartOutputs = chartOutputs.filter(
							(chartOutput) => chartOutput !== chartOutputForThisNumber,
						);
					}
					pairs.push(pair);
				});

				// add series that did not match any number
				chartOutputs.forEach((seriesOutput) => {
					pairs.push({
						chartOutput: seriesOutput,
					});
				});
			}
		});

		// fix up the titles for every one
		const outputs: DsDataOutputTopLevel[] = [];
		pairs.forEach((p) => {
			if (p.numberOutput) {
				outputs.push(p.numberOutput);
			}
			if (p.chartOutput) {
				outputs.push(p.chartOutput);
			}
		});

		return pairs;
	}

	private getSeries() {
		let allSeries: DChartSeries[] = [];

		// since we sort by numbers, get already sorted list with points and series outputs
		const numberPointAndChartOutputPairs = this.numberPointAndChartOutputPairs;

		interface ListItem {
			dimensionValues: DSData.DimensionsAndValuesMap;
			unit: string;
			series: DChartSeries;
			output: DsDataOutputTopLevel;
			secondarySeries: DChartSeries[];
		}

		// this list will need to get sorted after
		const allPrimarySeriesList: ListItem[] = [];
		let secondarySeriesWithoutPrimaryList: (ListItem & { primaryDs: DataSelect })[] = [];

		this.blockSetup.sortedDataSelectsShown.forEach((primaryDs) => {
			const primarySeriesList: ListItem[] = []; // so we can find ref by attribute / unit for the secondaries
			primaryDs.outputs.forEach((output) => {
				const series = this.createSeriesFromOutput(output);
				primarySeriesList.push({
					dimensionValues: output.dimensionValues,
					unit: output.unit,
					series,
					output,
					secondarySeries: [],
				});
			});
			const secondaryDs = this.blockSetup.getSecondaryDataSelectsForDataSelect(primaryDs);

			const primaryCompares = primaryDs.limitations.compareCMs;

			secondaryDs.forEach((ds) => {
				// skip numbers
				if (ds.dateRange.granulation?.isWholeRange) {
					return;
				}

				// exception for compare since we need to read the actual DsCompare from primary
				let titlePrefix = '';
				if (ds.isCompare) {
					const dsCompare = primaryCompares[0];
					if (dsCompare) {
						titlePrefix = dsCompare.titleUX;
					}
				}

				const secondaryOfSameType = secondaryDs.filter((sds) => sds.type === ds.type);

				// to add numbering to goal lines
				const dsIndex =
					secondaryOfSameType.length > 1 ? secondaryOfSameType.indexOf(ds) : undefined;

				ds.outputs.forEach((output) => {
					// find the matching primarySeries
					const primarySeriesItem = primarySeriesList.find((s) => {
						return (
							output.equalDimensionValues(s.dimensionValues) && s.unit === output.unit
						);
					});

					const primarySeries = primarySeriesItem?.series,
						series = this.createSeriesFromOutput(
							output,
							primarySeries,
							primaryDs,
							dsIndex,
						);

					if (titlePrefix) {
						series.titlePrefix = titlePrefix;
					}

					// Add anomaly series
					if (ds.isAnomaly && primarySeries) {
						primarySeries.points.forEach((primaryPoint) => {
							const anomalyPoint = series.points.find(
								(a) => a.index === primaryPoint.index,
							);
							if (anomalyPoint) {
								const anomalyData = {
									...anomalyPoint.anomalyData,
									...getAnomalyVariant(primaryPoint, anomalyPoint, output.invert),
								};
								const comparePoint = new DChartComparePoint({
									primaryPoint,
									value: anomalyPoint.value,
									valueStr: anomalyPoint.valueStr,
									compareSettings: { type: CompareType.ANOMALY },
									anomalyData,
								});
								primaryPoint.anomalyData = anomalyData;
								primaryPoint.comparePoints.push(comparePoint);
							}
						});
					}

					// Add Forecast items as compares on primary and main forecast where there is no primary
					if (ds.isOneOfForecasts) {
						// if forecasting future range we do not have primary series
						// we want to use the forecast series as primary
						const forecastSeries = secondarySeriesWithoutPrimaryList.find(
							(s) => s.series.type === DataSelectType.FORECAST,
						);
						const seriesUsedAsPrimary =
							primarySeries || forecastSeries?.series || series;
						if (seriesUsedAsPrimary) {
							seriesUsedAsPrimary.points.forEach((primaryPoint) => {
								const forecastPoint = series.points.find(
									(fp) => fp.index === primaryPoint.index,
								);
								if (forecastPoint) {
									const compareType = ds.compareType;
									let change: number | null = null,
										changeStr: string | undefined = undefined,
										indicatorClass: string | undefined = undefined;
									if (compareType === CompareType.FORECAST) {
										// add changeStr data for main vs forecast
										change = calculateChange(
											forecastPoint.value,
											primaryPoint.value,
											CompareFunction.PERCENTAGE,
										);
										changeStr = calculateAndFormatChange(
											forecastPoint.value,
											primaryPoint.value,
											'+0.#%',
											CompareFunction.PERCENTAGE,
										);
										if (typeof change === 'number') {
											indicatorClass = getIndicatorClass(
												change,
												output.invert,
											);
										}
									}

									const comparePoint = new DChartComparePoint({
										primaryPoint,
										value: forecastPoint.value,
										valueStr: forecastPoint.valueStr,
										change,
										changeStr,
										compareSettings: { type: ds.compareType },
										indicatorClass,
										granulationLabel: forecastPoint.granulationLabel,
										compareIndex: -1,
										title: series.titlePrefix, // for use in tooltip
									});
									primaryPoint.comparePoints.push(comparePoint);

									// sort - pessimistic, main forecast, optimistic
									primaryPoint.comparePoints.sort((a, b) => {
										if (
											a.compareType === CompareType.FORECAST_OPTIMISTIC ||
											b.compareType === CompareType.FORECAST_PESSIMISTIC
										) {
											return -1;
										} else if (
											a.compareType === CompareType.FORECAST_PESSIMISTIC ||
											b.compareType === CompareType.FORECAST_OPTIMISTIC
										) {
											return 1;
										}
										return 0;
									});
								}
								primaryPoint.recreateTooltip();
							});
						}
					}

					// all the compares either have to get connected to the primary item
					// or we add them to a separate list which we will add at the end of the ds
					if (primarySeriesItem) {
						primarySeriesItem.secondarySeries.push(series);
					} else {
						secondarySeriesWithoutPrimaryList.push({
							dimensionValues: output.dimensionValues,
							unit: output.unit,
							primaryDs: primaryDs,
							series: series,
							output: output,
							secondarySeries: [],
						});
					}
				});
			});

			allPrimarySeriesList.push(...primarySeriesList);
		});

		// sort allPrimarySeriesList by the numberPointAndChartOutputPairs order
		allPrimarySeriesList.sort((item1, item2) => {
			const outputPair1 = numberPointAndChartOutputPairs.find(
					(p) => p.chartOutput === item1.output,
				),
				outputPair2 = numberPointAndChartOutputPairs.find(
					(p) => p.chartOutput === item2.output,
				);
			if (!outputPair1 && !outputPair2) {
				return 0;
			} else if (!outputPair1) {
				return 1;
			} else if (!outputPair2) {
				return -1;
			} else {
				const index1 = numberPointAndChartOutputPairs.indexOf(outputPair1),
					index2 = numberPointAndChartOutputPairs.indexOf(outputPair2);
				if (index1 === index2) {
					return 0;
				} else {
					return index1 - index2;
				}
			}
		});

		// now that they're sorted, just filter out primary & secondary series from the List item
		// secondaries are after primaries, and all series of the same ds stick together
		allPrimarySeriesList.forEach((item, idx) => {
			allSeries.push(item.series, ...item.secondarySeries);

			// find those secondaries without primaries that belong to this same primary DS
			// add them at the very end of the series for this DS
			const primaryDs = item.output.dataRoot.dataSelect,
				nextPrimaryDs = allPrimarySeriesList[idx + 1]?.output.dataRoot.dataSelect;
			if (primaryDs !== nextPrimaryDs) {
				secondarySeriesWithoutPrimaryList
					.filter((item) => {
						return item.primaryDs === primaryDs;
					})
					.forEach((item) => {
						allSeries.push(item.series);
						secondarySeriesWithoutPrimaryList =
							secondarySeriesWithoutPrimaryList.filter((i) => i !== item);
					});
			}
		});
		// and add the rest of secondarySeriesWithoutPrimaryList that do not fit anywhere
		allSeries.push(...secondarySeriesWithoutPrimaryList.map((item) => item.series));

		// creating "aggregated" series (multiple shown as one)
		// we still keep the seperate series for tooltip reading, marked with isAggregatedIntoTotal
		// stacked compares keep their individual series with own colors (no aggregation)

		// goal/avg/trend/median of stackeds (per y-axis)
		const averagesOfStackedsLines: DChartSeries[][] = [], // by axis
			averagesOfStackedsBars: DChartSeries[][] = [],
			trendsOfStackedsLines: DChartSeries[][] = [],
			trendsOfStackedsBars: DChartSeries[][] = [],
			mediansOfStackedsLines: DChartSeries[][] = [],
			mediansOfStackedsBars: DChartSeries[][] = [],
			goalsOfStackedsLines: DChartSeries[][] = [],
			goalsOfStackedsBars: DChartSeries[][] = [];

		allSeries.forEach((s) => {
			if (!s.isAvgTrendMedianOrGoal || !s.primarySeries?.isStacked) {
				return;
			}

			const isPrimaryBar = s.primarySeries?.isBar;

			let pushTo;
			if (s.isAverage) {
				pushTo = isPrimaryBar ? averagesOfStackedsLines : averagesOfStackedsBars;
			} else if (s.isTrend) {
				pushTo = isPrimaryBar ? trendsOfStackedsLines : trendsOfStackedsBars;
			} else if (s.isMedian) {
				pushTo = isPrimaryBar ? mediansOfStackedsLines : mediansOfStackedsBars;
			} else if (s.isGoal) {
				pushTo = isPrimaryBar ? goalsOfStackedsLines : goalsOfStackedsBars;
			}

			if (pushTo) {
				const axisIndex = s.axisIndex;
				if (!pushTo[axisIndex]) {
					pushTo[axisIndex] = [];
				}
				pushTo[axisIndex].push(s);
			}
		});

		[
			averagesOfStackedsLines,
			averagesOfStackedsBars,
			trendsOfStackedsLines,
			trendsOfStackedsBars,
			mediansOfStackedsLines,
			mediansOfStackedsBars,
			goalsOfStackedsLines,
			goalsOfStackedsBars,
		].forEach((seriesByAxis) => {
			seriesByAxis.forEach((seriesGroup, axisIndex) => {
				const aggregatedSeries = this.createSeriesFromSeriesGroup(
					seriesGroup,
					'line',
					axisIndex,
				);

				allSeries = allSeries.filter((s) => !seriesGroup.includes(s));
				allSeries.push(aggregatedSeries);
			});
		});

		// SORTING THEM FOR VISUALIZATION
		// the sorting of primary was already done in the first part of the method (from number pairs)
		// the original series will be primary, then its secondaries,
		// then next primary and its secondaries, ..., and the aggregation(s) in the end
		// (it is set like that in visualization getSeriesForRenderer)

		// the order of rendering actual chart series should be the following:
		// for bars, the original stays (bar compares are next to their primaries)
		// for lines: goals, averages, trends, medians below, then all compares, then all primaries
		// (so all the primaries are above all the compares)

		// in the end, stacked lines + aggregated compares on the bottom
		// then bars, then unstacked lines
		// so the unstacked lines are above the bars

		const allBarSeries = allSeries.filter((s) => s.isBar),
			allLineSeries = allSeries.filter((s) => !s.isBar);

		const aggregatedCompareLines = allLineSeries.filter(
				(s) => s.isTheAggregatedTotal && s.isCompare,
			),
			stackedPrimaryLines = allLineSeries.filter((s) => s.isStacked && s.isMain),
			stackedCompareLines = allLineSeries.filter((s) => s.isStacked && s.isCompare),
			unstackedPrimaryLines = allLineSeries.filter((s) => !s.isStacked && s.isMain),
			unstackedCompareLines = allLineSeries.filter(
				(s) => !s.isStacked && s.isCompare && !s.isTheAggregatedTotal,
			),
			goalsAveragesAndTrendsAndMedians = allLineSeries.filter(
				(s) => s.isAvgTrendMedianOrGoal,
			),
			allAnomaly = allLineSeries.filter((s) => s.isAnomaly),
			allForecasts = allLineSeries.filter((s) => s.isOneOfForecasts);

		return [
			...aggregatedCompareLines,
			...stackedCompareLines,
			...stackedPrimaryLines,
			...allBarSeries,
			...goalsAveragesAndTrendsAndMedians,
			...unstackedCompareLines,
			...unstackedPrimaryLines,
			...allForecasts,
			...allAnomaly,
		];
	}

	private createSeriesFromOutput(
		output: DsDataOutputTopLevel,
		primarySeries?: DChartSeries,
		primaryDataSelect?: DataSelect,
		index?: number, // just for goal numbering
	) {
		const dataRoot = output.dataRoot,
			dataSelect = dataRoot.dataSelect;

		let isStacked = false;
		if (dataSelect.canStack) {
			isStacked =
				primarySeries?.isStacked || dataSelect.isStacked || !!primaryDataSelect?.isStacked;
		}

		const axisIndex = primarySeries
			? primarySeries.axisIndex
			: primaryDataSelect
				? primaryDataSelect.yAxisIndex
				: dataSelect.yAxisIndex;

		const showDataLabels = dataSelect.shownLabels === ShownLabels.VALUE;

		let typeToDraw = '';
		// there will be exception for compare lines that will be drawn as area
		// for charts where there is only 1 (or none) main series
		// check getSeries method for more info
		{
			const isSpecificallyBar =
				dataSelect.visualization === SeriesVisualization.BAR ||
				primaryDataSelect?.visualization === SeriesVisualization.BAR ||
				primarySeries?.isBar;

			const isSpecificallyLine =
				dataSelect.visualization === SeriesVisualization.LINE ||
				primaryDataSelect?.visualization === SeriesVisualization.LINE ||
				(primarySeries && !primarySeries.isBar);

			if (
				!dataSelect.isAlwaysLine &&
				(isSpecificallyBar ||
					(!isSpecificallyLine &&
						this.visualizationType === BlockVisualization.BAR_CHART))
			) {
				typeToDraw = 'column';
			} else {
				const isSpline = this.block?.isSpline;
				if (dataSelect.isMain || (isStacked && dataSelect.canStack)) {
					typeToDraw = isSpline ? 'areaspline' : 'area';
				} else {
					typeToDraw = isSpline ? 'spline' : 'line';
				}
			}

			if (dataSelect.isAnomaly) {
				typeToDraw = 'areasplinerange';
			}
		}

		let titlePrefix = '';
		if (dataSelect.isGoal) {
			titlePrefix = 'Goal';

			const titleIndex = typeof index === 'number' ? index + 1 : '';
			if (titleIndex) {
				titlePrefix += ` ${titleIndex}`;
			}
		}

		const points = this.createDChartPointsFromOutput(output);

		let title = output.shortTitle;
		if (dataSelect.isOneOfForecasts) {
			const typeText = DChartSeries.getTitleFromType(dataSelect.type);
			title = typeText; // for legend
			points.forEach((p) => {
				p.titleLong = typeText; // TODO: improve when multiple metrics are supported
			});
		} else if (dataSelect.isAnomaly) {
			title = 'Expected range';
		}

		const seriesData: SeriesData = {
			type: dataSelect.type,
			timezone: this.dataTimezone,
			title,
			titlePrefix,
			points,
			typeToDraw,
			granulationLabels: dataRoot.granulationLabels,
			axisIndex,
			isStacked,
			showDataLabels,
			baseId: `series-ds-${dataSelect.id}`, // TODO: more unique? add mk / attr / unit?
			metricSelectionData: output.metricSelectionData,
		};

		const series = new DChartSeries(seriesData);

		if (primarySeries) {
			series.primarySeries = primarySeries;
		}

		return series;
	}

	private createSeriesFromSeriesGroup(
		seriesGroup: DChartSeries[],
		typeToDraw: string,
		axisIndex: number,
	): DChartSeries {
		const firstSeries = seriesGroup[0],
			type = firstSeries?.type || DataSelectType.COMPARE,
			granulationLabels = firstSeries?.granulationLabels || [],
			pointValues: (number | null)[] = [];

		let format = '',
			scale: number | null = null;

		seriesGroup.forEach((series) => {
			series.points.forEach((p) => {
				if (typeof pointValues[p.index] !== 'number') {
					pointValues[p.index] = null;
				}
				if (p.hasValue) {
					if (!format) {
						format = p.format;
					}
					if (!scale) {
						scale = p.scale;
					}
					const value = pointValues[p.index] || 0;
					pointValues[p.index] = value + p.value!;
				}
			});
		});

		const points = pointValues.map((value, index) => {
			const pointData: PointData = {
				index,
				granulationLabel: granulationLabels[index],
				value,
				format,
				scale,
				dateRange: firstSeries?.points[0]?.dateRange,
			};
			return new DChartPrimaryPoint(pointData);
		});

		const aggregatedSeries = new DChartSeries({
			type,
			timezone: this.dataTimezone,
			title: `${DChartSeries.getTitleFromType(type)} (Aggregated)`,
			points,
			typeToDraw,
			granulationLabels,
			axisIndex,
			isStacked: false,
			showDataLabels: false,
			baseId: `series-aggregated-${axisIndex}-${type}`,
		});

		aggregatedSeries.isTheAggregatedTotal = true;

		return aggregatedSeries;
	}

	private prepareNumberPoints() {
		const points: DChartPrimaryPoint[] = [];
		this.numberPointAndChartOutputPairs.forEach((p) => {
			if (p.numberPoint) {
				points.push(p.numberPoint);
			}
		});
		return points;
	}

	// ----------- Detecting possible rendering changes -----------

	override get visualizationSettingsHash(): number {
		const block = this.block,
			dss = this.blockSetup.dataSelects;
		return hashCode(
			JSON.stringify({
				sp: block.isSpline,
				ysc: block.yAxisScale,
				l: block.legendPosition,
				sf: block.sortField,
				so: block.sortOrder,
				bc: block.colors,
				bst: block.showTotals,
				st: dss.map((ds) => ds.isStacked),
				va: dss.map((ds) => ds.shownLabels),
				vi: dss.map((ds) => ds.visualization),
				y: dss.map((ds) => ds.yAxisIndex),
				o: dss.map((ds) => ds.order), // bcos of possible custom sort
			}),
		);
	}

	// ------- general helpers --------

	getNumberForChartDs(dataSelect: DataSelect): DataSelect | null {
		const numberDs = this.blockSetup
			.getSecondaryDataSelectsForDataSelect(dataSelect)
			.find((item) => item.type === DataSelectType.MAIN);
		return numberDs || null;
	}

	getSecondarySeriesForChartDs(dataSelect: DataSelect): DataSelect[] {
		return this.blockSetup.getSecondaryDataSelectsForDataSelect(dataSelect).filter((ds) => {
			return ds.type !== DataSelectType.MAIN;
		});
	}

	override getTypeTextForDataSelect(dataSelect: DataSelect): string {
		if (dataSelect.limitations.isNumber) {
			return 'number';
		} else if (dataSelect.isCompare) {
			return 'compare';
		} else if (dataSelect.isGoal) {
			return 'goal';
		} else if (dataSelect.isAverage) {
			return 'average';
		} else if (dataSelect.isTrend) {
			return 'trend';
		} else if (dataSelect.isMedian) {
			return 'median';
		} else {
			return super.getTypeTextForDataSelect(dataSelect);
		}
	}

	// ------- visualization switch helpers --------

	override getExcessDataSelectsForSwitchTo(visualizationTo: VisualizationBase): DataSelect[] {
		// switching between line and bar is w/o changes
		if (visualizationTo.isLineChart || visualizationTo.isBarChart) {
			return [];
		}

		// getting rid of all number ds and secondary series
		const secondaryDs: DataSelect[] = [];
		this.blockSetup.primaryDataSelects.forEach((ds) => {
			const numDs = this.getNumberForChartDs(ds);
			if (numDs) {
				secondaryDs.push(numDs);
			}

			const secondarySeries = this.getSecondarySeriesForChartDs(ds);
			secondaryDs.push(...secondarySeries);
		});

		return secondaryDs;
	}

	override preCleanupForSwitchTo(visualizationTo: AnyVisualization): void {
		super.preCleanupForSwitchTo(visualizationTo);

		// remove visualization setting from ds
		this.blockSetup.primaryDataSelects.forEach((ds) => {
			ds.visualization = null;
		});
		this.loggerService.logToConsole(
			'[Vis change] from Line/Bar: remove all specific visualizations',
		);

		// switching between line and bar is w/o changes
		if (!visualizationTo.isLineChart && !visualizationTo.isBarChart) {
			// cleanup y scale settings
			this.block.yAxisScale = [];
			this.loggerService.logToConsole(
				'[Vis change] from Line/Bar: remove all y scale settings',
			);

			// remove 2nd y axis flag
			this.dataSelectsOnSecondAxis = [];
			this.loggerService.logToConsole('[Vis change] from Line/Bar: remove 2nd y axis flag');
		}
	}

	override updateForSwitchFrom(visualizationFrom: AnyVisualization): void {
		super.updateForSwitchFrom(visualizationFrom);

		// if switching from block that has no additional lines
		// and ds have compares, add "compare" lines
		if (!visualizationFrom.showAdditionalLines) {
			// create only after saving everything else so we know the DR on DS is definitely up-to-date
			void this.blockSetup.afterSaves().then(() => {
				this.blockSetup.dataSelectsShown.forEach((ds) => {
					this.blockSetup.addSecondaryDataSelectForDataSelect(ds, DataSelectType.COMPARE);
				});
			});
		}
	}

	// ------- MISC --------

	override getAllMainPoints() {
		return DChartSeries.getFirstPointsFromMainSeries(this.series);
	}

	static override recordLimit = RecordLimit;
}
